/*
 * ⌘K: la navegación real después de la primera semana. Busca cuentas, casos,
 * hallazgos y acciones a la vez. Se compara sin acentos: nadie escribe "Plática"
 * con tilde cuando tiene prisa.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCuentas } from "../api/hooks";
import { ETAPAS } from "../api/tipos";
import { Kbd } from "../ui/primitivas";

type Resultado = { clase: string; texto: string; ir: () => void };

function normalizar(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

export function Paleta({ abierta, cerrar, capturar }: { abierta: boolean; cerrar: () => void; capturar: (que: string) => void }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [marcado, setMarcado] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const { data: cuentas } = useCuentas();

  useEffect(() => {
    if (abierta) { setQ(""); setMarcado(0); setTimeout(() => input.current?.focus(), 10); }
  }, [abierta]);

  const todos = useMemo<Resultado[]>(() => {
    const acciones: Resultado[] = [
      { clase: "acción", texto: "Nueva cuenta", ir: () => capturar("cuenta") },
      { clase: "acción", texto: "Registrar interacción", ir: () => capturar("interaccion") },
      { clase: "vista", texto: "Ir a Hoy", ir: () => nav("/hoy") },
      { clase: "vista", texto: "Ir a Cuentas", ir: () => nav("/cuentas") },
      { clase: "vista", texto: "Tablero de descubrimiento", ir: () => nav("/descubrimiento") },
      { clase: "vista", texto: "Avisos de cambios", ir: () => nav("/notificaciones") },
    ];
    const cs: Resultado[] = (cuentas ?? []).map((c) => ({
      clase: "cuenta", texto: `${c.empresa} · ${ETAPAS[c.etapa].label}`, ir: () => nav(`/cuentas/${c.id}`),
    }));
    const verificar: Resultado[] = (cuentas ?? [])
      .flatMap((c) => c.casos.filter((k) => k.estado !== "D6" && k.estado !== "F2").map((k) => ({ c, k })))
      .map(({ c, k }) => ({ clase: "acción", texto: `Verificar hallazgos · ${c.empresa}`, ir: () => nav(`/verificacion/${k.id}`) }));
    // Primero las cuentas: "aldama" debe abrir Grupo Aldama, no una acción sobre ella.
    return [...cs, ...verificar, ...acciones];
  }, [cuentas, nav, capturar]);

  const resultados = useMemo(() => {
    if (!q) return [...todos.filter((r) => r.clase !== "cuenta"), ...todos.filter((r) => r.clase === "cuenta")].slice(0, 9);
    const ql = normalizar(q);
    return todos.filter((r) => normalizar(r.texto).includes(ql)).slice(0, 9);
  }, [q, todos]);

  if (!abierta) return null;
  const ir = (r: Resultado) => { cerrar(); r.ir(); };

  return (
    <div onClick={cerrar} style={{ position: "fixed", inset: 0, background: "rgba(11,29,58,.35)", zIndex: 100, display: "flex", justifyContent: "center", paddingTop: "14vh" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: "90vw", alignSelf: "flex-start", background: "var(--card)", borderRadius: "var(--r-card)", border: "1px solid var(--border)", boxShadow: "0 12px 40px rgba(11,29,58,.18)", overflow: "hidden" }}>
        <input ref={input} value={q} onChange={(e) => { setQ(e.target.value); setMarcado(0); }}
          onKeyDown={(e) => {
            if (e.key === "Escape") cerrar();
            else if (e.key === "ArrowDown") { e.preventDefault(); setMarcado((m) => Math.min(m + 1, resultados.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setMarcado((m) => Math.max(m - 1, 0)); }
            else if (e.key === "Enter" && resultados[marcado]) ir(resultados[marcado]);
          }}
          placeholder="Buscar cuentas, hallazgos, acciones…" aria-label="Buscar"
          style={{ width: "100%", border: "none", outline: "none", padding: "16px 18px", fontSize: 15, borderBottom: "1px solid var(--border)", background: "transparent" }} />
        <div style={{ maxHeight: 340, overflowY: "auto", padding: 6 }}>
          {resultados.length === 0 && <div style={{ padding: "14px 12px", color: "var(--faint)" }}>Sin resultados para «{q}»</div>}
          {resultados.map((r, i) => (
            <button key={i} onClick={() => ir(r)} onMouseEnter={() => setMarcado(i)}
              style={{ display: "flex", width: "100%", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: "var(--r-control)", background: i === marcado ? "var(--sunken)" : "transparent" }}>
              <span className="tag11" style={{ color: "var(--faint)", width: 70, flexShrink: 0 }}>{r.clase}</span>
              <span style={{ fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.texto}</span>
            </button>
          ))}
        </div>
        <div style={{ padding: "8px 14px", borderTop: "1px solid var(--border)", color: "var(--faint)", fontSize: 12, display: "flex", gap: 14 }}>
          <span><Kbd>↵</Kbd> abrir</span><span><Kbd>↑↓</Kbd> mover</span><span><Kbd>esc</Kbd> cerrar</span>
        </div>
      </div>
    </div>
  );
}
